import React, { useCallback, useEffect, useState } from 'react';
import sharedStyles from './AdminShared.module.scss';
import styles from './AdminPaymentDisputes.module.scss';
import { PaymentDisputeAlertSummaryDto } from '../../../types/admin.types';
import { fetchPaymentDisputeAlerts } from '../../../services/admin.service';
import { useUserState } from '../../../providers/user-state.provider.ts';

const emptySummary: PaymentDisputeAlertSummaryDto = {
    ActiveCount: 0,
    Alerts: []
};

const formatDateTime = (value?: string | null) => {
    if (!value) {
        return '-';
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const formatStatus = (status: string) => status.replace(/_/g, ' ');

const AdminPaymentDisputes = () => {
    const { getUserInfo } = useUserState();
    const token = getUserInfo()?.token;
    const [summary, setSummary] = useState(emptySummary);
    const [activeOnly, setActiveOnly] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const loadAlerts = useCallback(async () => {
        setIsLoading(true);
        setErrorMessage(null);
        try {
            setSummary(await fetchPaymentDisputeAlerts(activeOnly, 100, token));
        } catch {
            setErrorMessage('Unable to load payment dispute alerts.');
        } finally {
            setIsLoading(false);
        }
    }, [activeOnly, token]);

    useEffect(() => {
        loadAlerts();
    }, [loadAlerts]);

    return (
        <div className={sharedStyles.page}>
            <header className={sharedStyles.header}>
                <div>
                    <h1>Payment Disputes</h1>
                    <p>Review Stripe disputes that suspend or revoke video access.</p>
                </div>
                <button type="button" className={sharedStyles.secondary} onClick={loadAlerts} disabled={isLoading}>
                    Refresh
                </button>
            </header>

            <section className={`${sharedStyles.panel} ${sharedStyles.fullWidthPanel}`}>
                <div className={styles.summaryRow}>
                    <div>
                        <div className={styles.summaryValue}>{summary.ActiveCount}</div>
                        <div className={sharedStyles.muted}>Open disputes requiring review</div>
                    </div>
                    <label className={sharedStyles.inlineCheckbox}>
                        <input
                            type="checkbox"
                            checked={activeOnly}
                            onChange={(event) => setActiveOnly(event.target.checked)}
                        />
                        Show open disputes only
                    </label>
                </div>
            </section>

            {errorMessage ? <div className={styles.error} role="alert">{errorMessage}</div> : null}

            <section className={`${sharedStyles.panel} ${sharedStyles.fullWidthPanel}`}>
                <h2>Dispute alerts</h2>
                {isLoading ? (
                    <div className={sharedStyles.muted}>Loading payment disputes...</div>
                ) : summary.Alerts.length === 0 ? (
                    <div className={sharedStyles.empty}>No matching payment disputes.</div>
                ) : (
                    <div className={styles.tableScroll}>
                        <table className={sharedStyles.table}>
                            <thead>
                                <tr>
                                    <th>State</th>
                                    <th>Stripe dispute</th>
                                    <th>Video</th>
                                    <th>Opened</th>
                                    <th>Resolved</th>
                                </tr>
                            </thead>
                            <tbody>
                                {summary.Alerts.map((alert) => (
                                    <tr key={alert.Id}>
                                        <td>
                                            <span className={`${sharedStyles.status} ${alert.IsOpen ? styles.openStatus : styles.resolvedStatus}`}>
                                                {formatStatus(alert.DisputeStatus)}
                                            </span>
                                        </td>
                                        <td className={styles.identifier}>{alert.StripeDisputeId}</td>
                                        <td className={styles.identifier}>{alert.VideoId}</td>
                                        <td>{formatDateTime(alert.OpenedAtUtc)}</td>
                                        <td>{formatDateTime(alert.ResolvedAtUtc)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            <section className={`${sharedStyles.panel} ${sharedStyles.fullWidthPanel}`}>
                <h2>Entitlement policy reference</h2>
                <div className={styles.policyGrid}>
                    <article>
                        <h3>Promotional trials</h3>
                        <p>Stripe status <strong>trialing</strong> grants Subscriber access until the recorded period end. A paused trial removes access until Stripe reports it resumed.</p>
                    </article>
                    <article>
                        <h3>Coupons and discounts</h3>
                        <p>Stripe remains the source for the charged amount. Active subscriptions grant normal access, and a valid video Checkout with <strong>no payment required</strong> grants the video.</p>
                    </article>
                    <article>
                        <h3>Free videos</h3>
                        <p>A published video configured with price 0 is completed locally without calling Stripe. Its purchase row has amount 0 and no Stripe identifiers.</p>
                    </article>
                    <article>
                        <h3>Free account exceptions</h3>
                        <p>There is no separate free subscription plan yet. Retain the existing subscription through its recorded expiry; a manually assigned Subscriber role may be removed by Stripe reconciliation.</p>
                    </article>
                    <article>
                        <h3>Refunds</h3>
                        <p>A full video refund revokes that video. Partial video refunds retain it. Subscription refunds retain access only through the already-recorded expiry.</p>
                    </article>
                    <article>
                        <h3>Disputes</h3>
                        <p>An open dispute suspends the video, a win restores it, and a loss permanently revokes it. Replayed webhooks update the same alert.</p>
                    </article>
                </div>
            </section>
        </div>
    );
};

export default AdminPaymentDisputes;
